import { Color3, Constants, InstancedMesh, Matrix, Plane, StandardMaterial, Vector3, type AbstractMesh, type Material, type Mesh, type Scene, type TransformNode } from '@babylonjs/core';

/** How a character's silhouette is tinted. */
export type SilhouetteTone = 'ally' | 'enemy';

/** The rendering group silhouettes draw in: after the scene, against its depth and stencil. */
const SILHOUETTE_GROUP = 2;
/** What a character writes to the stencil wherever it is seen. */
const SEEN_STENCIL = 1;
const TONE_COLORS: Record<SilhouetteTone, Color3> = {
    ally: Color3.FromHexString('#8cc8ff'),
    enemy: Color3.FromHexString('#ff5a48'),
};
const SILHOUETTE_ALPHA = 0.55;
/**
 * Silhouettes are cut off at the floor (scene y 0): a foot or tail dipping a
 * little below it is hidden by the floor, not shown through it. Babylon
 * discards what lies on a clip plane's positive side, so this faces down.
 */
const BELOW_FLOOR = new Plane(0, -1, 0, 0);

/**
 * Shows characters hidden behind walls, props or anything else in the map as
 * a flat, see-through silhouette. Needs an engine with a stencil buffer.
 *
 * Every character mesh writes the stencil where it is seen. Each one also gets
 * a silhouette twin: a clone sharing its geometry and skeleton, parented to
 * it so it follows every pose and attachment, drawn after the scene with the
 * depth test reversed (only where something nearer covers it) and only where
 * the stencil says the character itself isn't seen. So a character's own arm
 * in front of its body never shows as a silhouette, and nor does one
 * character standing in front of another.
 */
export class CharacterSilhouettes {
    private readonly materials: Record<SilhouetteTone, StandardMaterial>;
    /** Per character key, each of its meshes' silhouette twin. */
    private readonly characters = new Map<string, Map<AbstractMesh, Mesh>>();
    private readonly twins = new Set<AbstractMesh>();
    private readonly stencilled = new WeakSet<Material>();

    public constructor(private readonly scene: Scene) {
        // Keep the scene's depth and stencil for the silhouettes to test against.
        scene.setRenderingAutoClearDepthStencil(SILHOUETTE_GROUP, false, false, false);
        this.materials = {
            ally: createSilhouetteMaterial(scene, 'ally'),
            enemy: createSilhouetteMaterial(scene, 'enemy'),
        };
    }

    /**
     * Keeps a character's silhouette in step with its meshes; call every frame
     * for every character shown. `tone` null hides its silhouette.
     */
    public sync(key: string, root: TransformNode, tone: SilhouetteTone | null): void {
        const twins = this.characters.get(key) ?? new Map<AbstractMesh, Mesh>();
        this.characters.set(key, twins);
        const sources = root.getChildMeshes(false).filter((mesh) => this.casts(mesh));
        const current = new Set(sources);
        for (const [source, twin] of twins) {
            if (!current.has(source) || source.isDisposed()) {
                this.disposeTwin(twin);
                twins.delete(source);
            }
        }
        for (const source of sources) {
            let twin = twins.get(source);
            if (!twin) {
                this.writeSeenStencil(source.material!);
                twin = this.createTwin(source);
                twins.set(source, twin);
            }
            twin.isVisible = tone !== null && source.isVisible && source.visibility > 0;
            if (tone !== null && twin.material !== this.materials[tone]) {
                twin.material = this.materials[tone];
            }
        }
    }

    /** Lets a character's silhouette go (when the character is removed). */
    public remove(key: string): void {
        this.characters.get(key)?.forEach((twin) => this.disposeTwin(twin));
        this.characters.delete(key);
    }

    public dispose(): void {
        [...this.characters.keys()].forEach((key) => this.remove(key));
        this.materials.ally.dispose();
        this.materials.enemy.dispose();
    }

    /** Solid character geometry casts a silhouette; glows, effects and twins don't. */
    private casts(mesh: AbstractMesh): boolean {
        return !this.twins.has(mesh)
            && mesh.renderingGroupId === 0
            && mesh.getTotalVertices() > 0
            && mesh.material !== null
            && !mesh.material.needAlphaBlending();
    }

    private writeSeenStencil(material: Material): void {
        if (this.stencilled.has(material)) {
            return;
        }
        this.stencilled.add(material);
        const stencil = material.stencil;
        stencil.enabled = true;
        stencil.func = Constants.ALWAYS;
        stencil.funcRef = SEEN_STENCIL;
        stencil.opStencilDepthPass = Constants.REPLACE;
        stencil.opStencilFail = Constants.KEEP;
        stencil.opDepthFail = Constants.KEEP;
        stencil.mask = 0xff;
    }

    private createTwin(source: AbstractMesh): Mesh {
        const geometry = source instanceof InstancedMesh ? source.sourceMesh : (source as Mesh);
        const twin = geometry.clone(`${source.name}-silhouette`, null, true);
        // A child of its source with no transform of its own lands exactly on it.
        twin.parent = source;
        twin.position = Vector3.Zero();
        twin.rotationQuaternion = null;
        twin.rotation = Vector3.Zero();
        twin.scaling = Vector3.One();
        twin.setPivotMatrix(Matrix.Identity());
        twin.skeleton = geometry.skeleton;
        twin.morphTargetManager = geometry.morphTargetManager;
        twin.alwaysSelectAsActiveMesh = source.alwaysSelectAsActiveMesh;
        twin.renderingGroupId = SILHOUETTE_GROUP;
        twin.isPickable = false;
        twin.setEnabled(true);
        this.twins.add(twin);
        return twin;
    }

    private disposeTwin(twin: Mesh): void {
        this.twins.delete(twin);
        if (!twin.isDisposed()) {
            twin.dispose();
        }
    }
}

function createSilhouetteMaterial(scene: Scene, tone: SilhouetteTone): StandardMaterial {
    const material = new StandardMaterial(`character-silhouette-${tone}`, scene);
    material.disableLighting = true;
    material.emissiveColor = TONE_COLORS[tone];
    material.diffuseColor = Color3.Black();
    material.specularColor = Color3.Black();
    material.alpha = SILHOUETTE_ALPHA;
    // Only where something nearer covers the character...
    material.depthFunction = Constants.GREATER;
    material.disableDepthWrite = true;
    material.clipPlane = BELOW_FLOOR;
    // ...and the character itself isn't seen. Marking the pixel seen keeps
    // overlapping silhouette layers from darkening each other.
    const stencil = material.stencil;
    stencil.enabled = true;
    stencil.func = Constants.NOTEQUAL;
    stencil.funcRef = SEEN_STENCIL;
    stencil.funcMask = 0xff;
    stencil.opStencilDepthPass = Constants.REPLACE;
    stencil.opStencilFail = Constants.KEEP;
    stencil.opDepthFail = Constants.KEEP;
    stencil.mask = 0xff;
    return material;
}
